<template>
  <GcBaseLayout
    :title="'Work Orders'"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="true"
    :backButtonFunction="onBackToList"
    :formsConfig="formsConfig"
    class="wo-bulk-update-wizard"
  >
    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Work Orders'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          draftState.transactionId ? 'Bulk Update' : 'New Bulk Update'
        "
        :secondaryLabel="draftState.transactionId || undefined"
        v-if="!loading"
      />
    </template>

    <div class="css-v2 wo-bulk-update-wizard-body" v-if="loaded">
      <!-- Sticky wizard header bar -->
      <div class="wizard-header-bar">
        <div class="wizard-header-center">
          <input
            v-model="draftState.description"
            :class="[
              'wizard-description-input',
              { 'has-error': saveAttempted && !draftState.description.trim() },
            ]"
            placeholder="Description"
            maxlength="200"
            :disabled="isReadOnly"
          />
          <span
            v-if="saveAttempted && !draftState.description.trim()"
            class="wizard-field-error"
          >
            Description is required
          </span>
        </div>
        <div class="wizard-header-right">
          <DxButton
            v-if="!isReadOnly && canEdit"
            :text="saving ? 'Saving...' : 'Save Draft'"
            type="default"
            styling-mode="contained"
            :disabled="saving || applying || isProcessing"
            @click="saveDraft"
          />
          <DxButton
            v-if="
              !isReadOnly && isDraft && canEdit && baseView.mode !== 'create'
            "
            text="Cancel transaction"
            type="danger"
            styling-mode="outlined"
            class="wizard-cancel-txn-btn"
            :disabled="applying"
            @click="cancelTransaction"
          />
          <DxButton
            :text="'Close'"
            type="normal"
            styling-mode="outlined"
            class="wizard-close-btn"
            @click="onBackToList(false)"
          />
        </div>
      </div>

      <!-- Loading state -->
      <div v-if="loading" class="wizard-loading-panel">
        <span>Loading transaction...</span>
      </div>

      <!-- Processing state — Apply in progress on the server -->
      <div v-else-if="isProcessing" class="wizard-loading-panel">
        <span
          >This transaction is currently being processed. Please wait...</span
        >
      </div>

      <!-- Audit record view — review mode -->
      <div v-else-if="isReadOnly" class="audit-record">
        <GcWoBulkUpdateAuditRecord
          :transaction-id="draftState.transactionId"
          :description="draftState.description"
          :status="draftState.status"
          :date-created="transactionMeta.DateCreated"
          :created-by="transactionMeta.CreatedBy"
          :date-committed="transactionMeta.DateCommitted"
          :committed-by="transactionMeta.CommittedBy"
          :wos-updated-count="transactionMeta.WosUpdatedCount"
          :wos-included-count="transactionMeta.WosIncludedCount"
          :search-criteria="draftState.searchCriteria"
          :material-update-config="draftState.materialUpdateConfig"
          :diff-snapshot="draftState.diffSnapshot"
          :matched-wo-list="draftState.matchedWoList"
          :excluded-wo-ids="draftState.excludedWoIds"
          :wo-outcomes="woOutcomes"
          :activity-log="activityLog"
        />
      </div>

      <!-- Wizard content — create / edit mode -->
      <template v-else>
        <!-- Step progress indicator -->
        <div class="wizard-steps-bar">
          <div
            v-for="(label, i) in stepLabels"
            :key="i"
            class="wizard-step-group"
          >
            <div
              :class="[
                'wizard-step',
                { 'is-active': currentStep === i + 1 },
                {
                  'is-done':
                    currentStep > i + 1 ||
                    (i + 1 === 2 && currentStep === 1 && canNavigateToStep2) ||
                    (i + 1 === 3 && currentStep === 2 && canNavigateToStep3),
                },
                { 'is-disabled': applying },
              ]"
              @click="!applying && onStepClick(i + 1)"
            >
              <span class="step-num">{{ i + 1 }}</span>
              {{ label }}
            </div>
            <span v-if="i < stepLabels.length - 1" class="step-div">›</span>
          </div>
        </div>

        <!-- Step content body -->
        <div class="wizard-step-body">
          <!-- Step 1: Search Criteria -->
          <GcWoBulkUpdateStepSearch
            v-show="currentStep === 1"
            :isReadOnly="isReadOnly"
            :savedCriteria="draftState.searchCriteria"
            @search-complete="onSearchComplete"
            @criteria-change="onSearchCriteriaChange"
            :baseView="baseView"
            :showLoading="showLoading"
            @go-next="goNext"
          />

          <!-- Step 2: Configure -->
          <GcWoBulkUpdateStepConfigure
            ref="configureStep"
            v-show="currentStep === 2"
            :matchedWoList="draftState.matchedWoList"
            :excludedWoIds="draftState.excludedWoIds"
            :savedCriteria="draftState.searchCriteria"
            :materialUpdateConfig="draftState.materialUpdateConfig"
            :isReadOnly="isReadOnly"
            @excluded-ids-change="onExcludedIdsChange"
            @material-config-update="onMaterialConfigUpdate"
            @validation-change="onConfigureValidationChange"
          />

          <!-- Step 3: Preview diff -->
          <GcWoBulkUpdateStaleDiffBanner
            v-if="staleDiffWarningActive && draftState.diffComputedAt"
            v-show="currentStep === 3"
            :affected-wo-count="staleDiffAffectedWoCount"
            :detail="staleDiffDetail"
            :stale-wos="staleWos"
            :is-read-only="isReadOnly"
            @recompute="onStaleDiffRecompute"
            @acknowledge="onStaleDiffAcknowledge"
          />
          <GcWoBulkUpdateStepPreview
            ref="previewStep"
            v-show="currentStep === 3"
            :active="currentStep === 3"
            :matched-wo-list="draftState.matchedWoList"
            :excluded-wo-ids="draftState.excludedWoIds"
            :material-update-config="draftState.materialUpdateConfig"
            :company-code="companyCode"
            :transaction-id="draftState.transactionId"
            :diff-snapshot="draftState.diffSnapshot"
            :is-read-only="isReadOnly"
            :search-criteria="draftState.searchCriteria"
            @diff-computed="onDiffComputed"
            @exclude-wo="onExcludeWoFromPreview"
            @include-wo="onIncludeWoFromPreview"
            @auto-exclude-zero-affected="onAutoExcludeZeroAffected"
          />

          <!-- Step 4: Apply -->
          <GcWoBulkUpdateStepApply
            ref="applyStep"
            v-show="currentStep === 4"
            :active="currentStep === 4"
            :wos-to-update="includedWoCount"
            :wos-excluded="draftState.excludedWoIds.length"
            :material-lines-to-change="computedMaterialLinesToChange"
            :is-read-only="isReadOnly"
            :applying="applying"
            :stale-diff-warning-active="staleDiffWarningActive"
            :stale-diff-affected-wo-count="staleDiffAffectedWoCount"
            :stale-diff-detail="staleDiffDetail"
            :stale-wos="staleWos"
            :diff-computed-at="draftState.diffComputedAt"
            @go-back="goBack"
            @apply="onApplyStep"
            @stale-diff-acknowledge="onStaleDiffAcknowledge"
            @stale-diff-recompute="onStaleDiffRecompute"
          />
        </div>
      </template>
    </div>

    <!-- Footer navigation in GcBaseLayout #footer slot — renders outside the scroll view, always at bottom -->
    <template #footer>
      <div
        v-if="loaded && !isReadOnly && (currentStep === 2 || currentStep === 3)"
        class="wizard-footer-bar"
      >
        <DxButton
          :text="currentStep === 3 ? 'Back to Configure' : 'Back'"
          type="normal"
          styling-mode="outlined"
          :disabled="applying"
          @click="goBack"
        />
        <DxButton
          :text="currentStep === 3 ? 'Proceed to Apply' : 'Preview Changes'"
          type="default"
          styling-mode="contained"
          :disabled="!canAdvance || applying"
          class="wizard-next-btn"
          @click="goNext"
        />
      </div>
    </template>
  </GcBaseLayout>
</template>

<script>
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import DxButton from "devextreme-vue/button";
import { mapGetters } from "vuex";
import { WoBulkUpdateService } from "@/_services/api/production/wo-bulk-update.service";
import { DefaultCompanyBranchDivisionService } from "@/_services/api/sales/default-company-branch-dicision.service";
import { IsResponseDataValid } from "@/_helpers/ui/helpers";
import { FeatureStorage } from "@/_services/storage";

export default {
  name: "work-order-bulk-update",
  mixins: [RouteMixin, FormMixin],
  components: {
    DxButton,
    GcHeaderContentDynamic: () =>
      import("@/components/gc-header-content-dynamic"),
    GcWoBulkUpdateStepSearch: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-step-search"
      ),
    GcWoBulkUpdateStepConfigure: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-step-configure"
      ),
    GcWoBulkUpdateStepPreview: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-step-preview"
      ),
    GcWoBulkUpdateStaleDiffBanner: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-stale-diff-banner"
      ),
    GcWoBulkUpdateStepApply: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-step-apply"
      ),
    GcWoBulkUpdateAuditRecord: () =>
      import(
        "@/components/production/work-order-bulk-update/gc-wo-bulk-update-audit-record"
      ),
  },
  data() {
    return {
      loading: false,
      loaded: false,
      saving: false,
      saveAttempted: false,
      applying: false,
      currentStep: 1,
      configureStepValid: false,
      stepLabels: ["Search", "Configure", "Preview Changes", "Apply"],
      draftState: {
        transactionId: null,
        description: "",
        status: "D",
        searchCriteria: {
          materialCriteria: [],
          scopeFilters: {},
        },
        matchedWoList: [],
        selectedWoIds: [],
        excludedWoIds: [],
        materialUpdateConfig: null,
        diffSnapshot: null,
        diffComputedAt: null,
        staleDiffAcknowledged: false,
        staleDiffAcknowledgedNote: "",
      },
      searchResultsStale: false,
      configChangedSincePreview: false,
      allLinesNotApplicable: false,
      staleDiffWarningActive: false,
      staleDiffChecking: false,
      staleDiffAffectedWoCount: 0,
      staleDiffDetail: null,
      staleWos: [],
      recomputePending: false,
      companyCode: null,
      activityLog: [],
      woOutcomes: [],
      lastSavedDraftState: null,
      transactionMeta: {
        DateCreated: null,
        CreatedBy: null,
        DateCommitted: null,
        CommittedBy: null,
        WosUpdatedCount: 0,
        WosIncludedCount: 0,
      },
    };
  },
  computed: {
    ...mapGetters({ getHotKeySave: "hotKey/getHotKeySave" }),
    isReadOnly() {
      return ["C", "CE", "X"].includes(this.draftState.status);
    },
    isProcessing() {
      return this.draftState.status === "P";
    },
    isDraft() {
      return this.draftState.status === "D";
    },
    canEdit() {
      return FeatureStorage.checkPermission("BulkWOUpdate", "create");
    },
    canNavigateToStep2() {
      return (
        this.draftState.matchedWoList &&
        this.draftState.matchedWoList.length > 0 &&
        !this.searchResultsStale
      );
    },
    canNavigateToStep3() {
      return (
        !!this.draftState.diffComputedAt && !this.configChangedSincePreview
      );
    },
    canAdvance() {
      if (this.currentStep === 1) {
        return this.draftState.matchedWoList.length > 0;
      }
      if (this.currentStep === 2) {
        return this.includedWoCount > 0 && this.configureStepValid;
      }
      if (this.currentStep === 3) {
        return (
          this.includedWoCount > 0 &&
          !this.allLinesNotApplicable &&
          !this.staleDiffChecking &&
          !this.staleDiffWarningActive
        );
      }
      return false;
    },
    includedWoCount() {
      return this.draftState.matchedWoList.filter(
        (wo) => !this.draftState.excludedWoIds.includes(wo.Wono),
      ).length;
    },
    hasMaterialCriteria() {
      const sc = this.draftState.searchCriteria;
      return (sc.materialCriteria || sc.MaterialCriteria || []).length > 0;
    },
    computedMaterialLinesToChange() {
      const snapshot = this.draftState.diffSnapshot;
      if (!snapshot || !snapshot.WoDiffs) return 0;
      const excludedSet = this.draftState.excludedWoIds;
      let count = 0;
      snapshot.WoDiffs.forEach((wo) => {
        if (excludedSet.includes(wo.Wono)) return;
        (wo.Lines || []).forEach((line) => {
          if (
            line.CanApply &&
            line.ChangeType &&
            line.ChangeType !== "NoChange"
          ) {
            count++;
          }
        });
      });
      return count;
    },
  },
  async created() {
    this.showLoading(true);
    const mode = this.baseView.mode;
    const id = this.baseView.id;

    DefaultCompanyBranchDivisionService.getDefault().then((res) => {
      this.companyCode = res.data.value[0].CompanyCode;
    });

    if (mode === "create") {
      this.currentStep = 1;
      this.showLoading(false);
    } else if (mode === "edit") {
      await this.loadDraft(id);
      if (
        (this.currentStep === 3 || this.currentStep === 4) &&
        this.draftState.diffComputedAt
      ) {
        await this.checkStaleDiff();
      }
      this.showLoading(false);
    } else if (mode === "review") {
      await this.loadAuditRecord(id);
      this.showLoading(false);
    } else {
      this.showLoading(false);
    }

    this.$nextTick(() => {
      this.loaded = true;
    });
  },
  methods: {
    // ─── Navigation ────────────────────────────────────────────────────────────

    async goNext() {
      if (!this.canAdvance) return;
      if (this.currentStep === 2) {
        var valid =
          this.$refs.configureStep && this.$refs.configureStep.validate();
        if (!valid) return;
        // Force a fresh diff whenever advancing to Preview
        this.draftState.diffSnapshot = null;
        this.draftState.diffComputedAt = null;
        this.allLinesNotApplicable = false;
        if (this.$refs.previewStep) this.$refs.previewStep.reset();
      }
      if (this.currentStep === 3) {
        // Persist transactionId, diffSnapshot and DiffComputedAt before entering
        // Apply — /Apply is keyed by TransactionId and reads the persisted draft,
        // not in-memory state, so an unsaved transaction (or stale diff) would fail.
        var saved = await this.saveDraft();
        if (!saved) return;
      }
      this.currentStep += 1;
      if (this.currentStep === 4) {
        this.checkStaleDiff();
      }
    },
    goBack() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    onStepClick(step) {
      if (
        step < this.currentStep ||
        (step === 2 && this.canNavigateToStep2) ||
        (step === 3 && this.canNavigateToStep3)
      ) {
        this.currentStep = step;
        if (step === 3) {
          this.checkStaleDiff();
        }
      }
    },
    onBackToList() {
      this.$router.push({ name: "work-order-bulk-update" });
    },

    // ─── Search step events ────────────────────────────────────────────────────

    onSearchComplete(payload) {
      this.draftState.matchedWoList = payload.matchedWoList;
      this.draftState.searchCriteria = payload.searchCriteria;
      this.draftState.selectedWoIds = payload.matchedWoList.map(
        (wo) => wo.Wono,
      );
      this.draftState.excludedWoIds = [];
      this.draftState.materialUpdateConfig = null;
      this.searchResultsStale = false;
      this.configChangedSincePreview = false;
      this.allLinesNotApplicable = false;
      this.configureStepValid = false;
      this.currentStep = 2;
    },
    onSearchCriteriaChange(criteria) {
      this.draftState.searchCriteria = criteria;
      this.searchResultsStale = true;
    },
    onExcludedIdsChange(excludedWoIds) {
      this.draftState.excludedWoIds = excludedWoIds;
      this.draftState.selectedWoIds = this.draftState.matchedWoList
        .map((wo) => wo.Wono)
        .filter((id) => !excludedWoIds.includes(id));
      if (this.draftState.diffComputedAt) {
        this.configChangedSincePreview = true;
      }
    },

    // ─── Configure step events ─────────────────────────────────────────────────

    onMaterialConfigUpdate(config) {
      this.draftState.materialUpdateConfig = config;
      if (this.draftState.diffComputedAt) {
        this.configChangedSincePreview = true;
      }
    },
    onConfigureValidationChange(e) {
      this.configureStepValid = e && e.isValid;
    },

    // ─── Preview step — exclude / include WO ──────────────────────────────────

    onExcludeWoFromPreview(wono) {
      if (this.draftState.excludedWoIds.indexOf(wono) === -1) {
        this.draftState.excludedWoIds = this.draftState.excludedWoIds.concat([
          wono,
        ]);
        this.draftState.selectedWoIds = this.draftState.selectedWoIds.filter(
          function (id) {
            return id !== wono;
          },
        );
      }
    },
    onIncludeWoFromPreview(wono) {
      this.draftState.excludedWoIds = this.draftState.excludedWoIds.filter(
        function (id) {
          return id !== wono;
        },
      );
      if (this.draftState.selectedWoIds.indexOf(wono) === -1) {
        this.draftState.selectedWoIds = this.draftState.selectedWoIds.concat([
          wono,
        ]);
      }
    },

    onAutoExcludeZeroAffected(wonos) {
      this.draftState.excludedWoIds = [
        ...new Set([...this.draftState.excludedWoIds, ...wonos]),
      ];
      this.draftState.selectedWoIds = this.draftState.selectedWoIds.filter(
        (id) => !wonos.includes(id),
      );
    },

    // ─── Diff computed ────────────────────────────────────────────────────────

    async onDiffComputed(payload) {
      this.draftState.diffSnapshot = payload.diffSnapshot;
      this.draftState.diffComputedAt = payload.diffComputedAt;
      this.allLinesNotApplicable = payload.allLinesNotApplicable || false;
      this.configChangedSincePreview = false;
      // Fresh compute clears any previous stale state, then immediately re-check
      this.staleDiffWarningActive = false;
      this.staleDiffAffectedWoCount = 0;
      this.staleDiffDetail = null;
      this.staleWos = [];
      this.draftState.staleDiffAcknowledged = false;
      if (this.recomputePending) {
        this.recomputePending = false;
        this.activityLog.push({
          Action: "DiffRecomputed",
          Timestamp: payload.diffComputedAt || new Date().toISOString(),
        });
        await this.saveDraft(); // silent save to persist new snapshot and DiffComputedAt
      }
      this.$nextTick(() => {
        this.checkStaleDiff();
      });
    },

    // ─── Stale diff ────────────────────────────────────────────────────────────

    async checkStaleDiff() {
      if (this.draftState.staleDiffAcknowledged) return;
      if (
        !this.draftState.diffComputedAt ||
        !this.draftState.diffSnapshot ||
        !this.draftState.diffSnapshot.WoDiffs ||
        !this.draftState.selectedWoIds.length
      )
        return;
      this.staleDiffChecking = true;
      try {
        var payload = {
          CompanyCode: this.companyCode,
          SelectedWoIds: this.draftState.selectedWoIds,
          DiffComputedAt: this.draftState.diffComputedAt,
          DiffSnapshot: this.draftState.diffSnapshot.WoDiffs,
          MaterialUpdateConfig: this.draftState.materialUpdateConfig,
        };

        // MaterialSearchCriteria — UpdateQtyAll only, mirrors ComputeDiff
        var matConfig = this.draftState.materialUpdateConfig;
        var matCriteria = this.draftState.searchCriteria;

        if (
          matConfig &&
          matConfig.Operation === "UpdateQtyAll" &&
          matCriteria &&
          matCriteria.MaterialCriteria &&
          matCriteria.MaterialCriteria.length
        ) {
          payload.MaterialSearchCriteria = matCriteria.MaterialCriteria;
        }

        var res = await WoBulkUpdateService.checkStaleDiff(payload);
        this.staleDiffAffectedWoCount = res.data.AffectedWoCount || 0;
        this.staleDiffDetail = res.data.Detail || null;
        this.staleWos = res.data.StaleWos || [];
        this.staleDiffWarningActive = !!res.data.IsStale;
        this.draftState.staleDiffAcknowledged = false;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("Stale diff check failed", e);
      } finally {
        this.staleDiffChecking = false;
      }
    },
    async onStaleDiffAcknowledge(note) {
      this.draftState.staleDiffAcknowledged = true;
      this.draftState.staleDiffAcknowledgedNote = note || "";
      this.staleDiffWarningActive = false;
      await this.saveDraft(); // silent save to persist acknowledgement in draft
      this.activityLog.push({
        Action: "StaleDiffAcknowledged",
        Timestamp: new Date().toISOString(),
        Notes: note || "",
      });
    },
    async onStaleDiffRecompute() {
      await this.saveDraft(); // silent save to persist current state before recompute
      this.staleDiffWarningActive = false;
      this.staleDiffAffectedWoCount = 0;
      this.staleDiffDetail = null;
      this.staleWos = [];
      this.draftState.diffSnapshot = null;
      this.draftState.diffComputedAt = null;
      this.draftState.staleDiffAcknowledged = false;
      this.recomputePending = true;
      if (this.$refs.previewStep) {
        this.$refs.previewStep.reset();
        this.$nextTick(async () => {
          await this.$refs.previewStep.initDiff();
        });
      }
    },

    // ─── Apply ─────────────────────────────────────────────────────────────────

    async onApplyStep() {
      if (this.applying) return;
      this.applying = true;
      this.showLoading(true);
      try {
        await WoBulkUpdateService.apply(this.draftState.transactionId);
        await this.loadAuditRecord(this.draftState.transactionId);
        this.setIgnoreRouteChangeConfirmation();

        this.$router.replace({
          name: "work-order-bulk-update-review",
          params: { id: this.draftState.transactionId },
        });
      } catch (err) {
        console.error("Apply failed", err);
        this.displayServerError(
          err,
          "Failed to apply bulk update. Please try again.",
        );
      } finally {
        this.applying = false;
        this.showLoading(false);
      }
    },

    // ─── Save Draft ────────────────────────────────────────────────────────────

    async saveDraft() {
      if (this.isReadOnly) return;
      this.saveAttempted = true;
      if (!this.draftState.description.trim()) return;
      this.showLoading(true);
      try {
        this.saving = true;
        const payload = this.buildDraftPayload();
        const res = await WoBulkUpdateService.saveDraft(payload);
        this.draftState.transactionId = res.data.TransactionId;

        if (this.baseView.mode === "create") {
          this.setIgnoreRouteChangeConfirmation();
          this.$router.replace({
            name: "work-order-bulk-update-edit",
            params: { id: res.data.TransactionId },
          });
        }

        if (res.data.ActivityLog) {
          this.activityLog = res.data.ActivityLog;
        }

        this.lastSavedDraftState = JSON.parse(JSON.stringify(this.draftState));
        this.$notify("Draft saved — " + res.data.TransactionId, "success");
        return true;
      } catch (err) {
        this.showLoading(false);
        this.displayServerError(err, "Failed to save draft. Please try again.");
      } finally {
        this.showLoading(false);
        this.saving = false;
      }
    },
    buildDraftPayload() {
      return {
        TransactionId: this.draftState.transactionId,
        CompanyCode: this.companyCode,
        Description: this.draftState.description,
        CurrentStep: this.currentStep,
        SearchCriteriaJson: JSON.stringify(this.draftState.searchCriteria),
        MatchedWoListJson: JSON.stringify(this.draftState.matchedWoList),
        SelectedWoIdsJson: JSON.stringify(this.draftState.selectedWoIds),
        ExcludedWoIdsJson: JSON.stringify(this.draftState.excludedWoIds),
        MaterialUpdateConfigJson: JSON.stringify(
          this.draftState.materialUpdateConfig,
        ),
        DiffSnapshotJson: JSON.stringify(this.draftState.diffSnapshot),
        DiffComputedAt: this.draftState.diffComputedAt,
        StaleDiffAcknowledged: this.draftState.staleDiffAcknowledged,
        StaleDiffAcknowledgedNote:
          this.draftState.staleDiffAcknowledgedNote || null,
      };
    },

    // ─── Load Draft (edit mode) ────────────────────────────────────────────────

    async loadDraft(transactionId) {
      try {
        this.loading = true;
        const res = await WoBulkUpdateService.getTransaction(transactionId);
        if (IsResponseDataValid(res)) {
          const t = res.data.value[0];

          if (t.CompanyCode) {
            this.companyCode = t.CompanyCode;
          }

          const matchedWoList = this.parseJson(t.MatchedWoListJson, []);

          this.draftState = {
            transactionId: t.TransactionId,
            description: t.Description || "",
            status: t.Status.trim(),
            searchCriteria: this.parseJson(t.SearchCriteriaJson, {
              materialCriteria: [],
              scopeFilters: {},
            }),
            matchedWoList: matchedWoList,
            selectedWoIds: this.parseJson(t.SelectedWoIdsJson, []),
            excludedWoIds: this.parseJson(t.ExcludedWoIdsJson, []),
            materialUpdateConfig: this.parseJson(
              t.MaterialUpdateConfigJson,
              null,
            ),
            diffSnapshot: this.parseJson(t.DiffSnapshotJson, null),
            diffComputedAt: t.DiffComputedAt || null,
            staleDiffAcknowledged: false,
            staleDiffAcknowledgedNote: "",
          };

          this.currentStep = t.CurrentStep || 1;

          if (t.ActivityLog) {
            this.activityLog = t.ActivityLog;
          }

          this.$nextTick(() => {
            this.lastSavedDraftState = JSON.parse(
              JSON.stringify(this.draftState),
            );
          });
        } else {
          this.$notify(
            "Transaction not found or could not be loaded.",
            "error",
          );
          this.$router.push({ name: "work-order-bulk-update" });
        }
      } catch (err) {
        this.displayServerError(
          err,
          "Failed to load transaction. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },

    // ─── Load Audit Record (review mode) ──────────────────────────────────────

    async loadAuditRecord(transactionId) {
      try {
        this.loading = true;
        const res = await WoBulkUpdateService.getTransactionRecord(
          transactionId,
        );
        if (res && res.data) {
          const t = res.data;
          this.transactionMeta = {
            DateCreated: t.DateCreated || null,
            CreatedBy: t.CreatedBy || null,
            DateCommitted: t.DateCommitted || null,
            CommittedBy: t.CommittedBy || null,
            WosUpdatedCount: t.WosUpdatedCount || 0,
            WosIncludedCount: t.WosIncludedCount || 0,
          };
          this.draftState = {
            transactionId: t.TransactionId,
            description: t.Description || "",
            status: t.Status.trim(),
            searchCriteria: t.SearchCriteria || {
              materialCriteria: [],
              scopeFilters: {},
            },
            matchedWoList: t.MatchedWoList || [],
            selectedWoIds: t.SelectedWoIds || [],
            excludedWoIds: t.ExcludedWoIds || [],
            materialUpdateConfig: t.MaterialUpdateConfig || null,
            diffSnapshot: t.DiffSnapshot || null,
            diffComputedAt: t.DiffComputedAt || null,
            staleDiffAcknowledged: false,
            staleDiffAcknowledgedNote: "",
          };
          this.woOutcomes = t.WoOutcomes || [];

          if (t.ActivityLog) {
            this.activityLog = t.ActivityLog;
          }
        }
      } catch (err) {
        this.displayServerError(
          err,
          "Failed to load audit record. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },

    // ─── Cancel transaction ────────────────────────────────────────────────────

    async cancelTransaction() {
      if (this.isReadOnly || !this.isDraft) return;
      const confirmed = await this.$confirm(
        "Are you sure you want to cancel this transaction? This action cannot be undone.",
        "Cancel Transaction",
      );
      if (!confirmed) return;

      this.showLoading(true);
      try {
        await WoBulkUpdateService.cancel(this.draftState.transactionId);
        this.$notify("Transaction cancelled.", "success");
        this.setIgnoreRouteChangeConfirmation();
        this.$router.push({ name: "work-order-bulk-update" });
      } catch (err) {
        this.showLoading(false);
        this.displayServerError(
          err,
          "Failed to cancel transaction. Please try again.",
        );
      } finally {
        this.showLoading(false);
      }
    },

    // ─── Helpers ───────────────────────────────────────────────────────────────

    parseJson(str, fallback) {
      if (!str) return fallback;
      try {
        return JSON.parse(str);
      } catch (e) {
        return fallback;
      }
    },
  },

  watch: {
    getHotKeySave(newVal) {
      if (newVal) {
        this.$store.dispatch("hotKey/setHotKeySave", false);
        this.saveDraft();
      }
    },
  },
};
</script>

<style lang="scss">
.wo-bulk-update-wizard {
  .wo-bulk-update-wizard-body {
    padding: 12px;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  .wizard-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    border-bottom: 1px solid #dee2e6;
    background-color: #fff;
    gap: 16px;
  }

  .wizard-header-center {
    flex: 1;
  }

  .wizard-description-input {
    width: 100%;
    max-width: 500px;
    padding: 8px 12px;
    border: 1px solid #d0d5dd;
    border-radius: 6px;
    font-size: 13px;
    color: #344054;

    &::placeholder {
      color: #98a2b3;
    }

    &.has-error {
      border-color: #d92d20;
      background-color: #fef3f2;
    }
  }

  .wizard-field-error {
    display: block;
    color: #d92d20;
    font-size: 12px;
    margin-top: 4px;
  }

  .wizard-header-right {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
  }

  .coming-soon-banner {
    padding: 48px 32px;
    margin-bottom: 32px;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
    animation: slideDown 0.5s ease-out;

    .banner-content {
      display: flex;
      align-items: flex-start;
      gap: 32px;
      max-width: 900px;
      margin: 0 auto;
    }

    .banner-icon {
      font-size: 64px;
      line-height: 1;
      flex-shrink: 0;
      animation: bounce 2s ease-in-out infinite;
    }

    .banner-text {
      flex: 1;
      color: #fff;

      h2 {
        margin: 0 0 12px 0;
        font-size: 28px;
        font-weight: 600;
        line-height: 1.2;
      }

      p {
        margin: 0 0 20px 0;
        font-size: 14px;
        line-height: 1.6;
        opacity: 0.95;

        &.coming-soon-note {
          margin-top: 16px;
          font-size: 13px;
          font-style: italic;
          opacity: 0.85;
        }
      }
    }

    .feature-list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: 12px;
      margin-bottom: 8px;
    }

    .feature-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px;
      background-color: rgba(255, 255, 255, 0.1);
      border-radius: 4px;
      font-size: 13px;
      backdrop-filter: blur(10px);

      .icon {
        font-size: 16px;
        flex-shrink: 0;
      }

      span {
        line-height: 1.3;
      }
    }
  }

  // ── Step body ──────────────────────────────────────────────────────────────
  .wizard-step-body {
    flex: 1;
    padding-top: 10px;
    min-height: 0;

    .step-placeholder {
      min-height: 200px;
    }
  }

  .wizard-steps-bar {
    display: flex;
    align-items: center;
    padding: 0 16px;
    border-bottom: 1px solid #dee2e6;
    background: #fff;
  }

  .wizard-step-group {
    display: flex;
    align-items: center;
  }

  .wizard-step {
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0 12px;
    gap: 8px;
    font-size: 12px;
    font-weight: 500;
    color: #adb5bd;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    cursor: default;

    .step-num {
      width: 20px;
      height: 20px;
      border-radius: 50%;
      border: 1.5px solid currentColor;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;
      flex-shrink: 0;
    }

    &.is-active {
      color: #03a9f4;
      border-bottom-color: #03a9f4;
    }

    &.is-done {
      color: #17b26a;
      cursor: pointer;

      .step-num {
        background: #17b26a;
        border-color: #17b26a;
        color: #fff;
      }

      &:hover {
        opacity: 0.8;
      }

      &.is-disabled {
        color: #adb5bd;
        cursor: default;
        opacity: 0.6;

        .step-num {
          background: transparent;
          border-color: currentColor;
          color: #adb5bd;
        }

        &:hover {
          opacity: 0.6;
        }
      }
    }

    &.is-disabled {
      color: #adb5bd;
      cursor: default;
      opacity: 0.6;

      &:hover {
        opacity: 0.6;
      }
    }
  }

  .step-div {
    color: #dee2e6;
    font-size: 16px;
    margin: 0 -4px;
  }

  .wizard-loading-panel {
    padding: 32px;
    text-align: center;
    color: #98a2b3;
    font-size: 13px;
  }

  .step-placeholder {
    padding: 32px;
    text-align: center;
    color: #98a2b3;

    h3 {
      margin-bottom: 16px;
      color: #344054;
      font-size: 14px;
    }

    p {
      font-size: 13px;
    }
  }

  .wizard-footer-bar {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding: 12px 16px;
    border-top: 1px solid #dee2e6;
    background-color: #fff;
  }

  .wizard-next-btn {
    margin-left: 8px;
  }
}
</style>
